import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { Plus, Trash2, Save, BookOpen } from "lucide-react";

const blankGuide = (slug) => ({
  slug, title: "",
  quickstart: { summary: "", bullets: [] },
  reconstitution: { vial_mg: 5, default_water_ml: 2, default_dose_mcg: 250, frequency: "", note: "", steps: [] },
  supplies: { plan: "", items: [] },
  protocol_overview: [], dosing: [], storage: [], important_notes: [],
  how_it_works: "", benefits: [], side_effects: [], lifestyle: [],
  injection_technique: [], faq: [], references: [],
});

const Label = ({ children }) => (
  <div className="text-[10px] font-mono uppercase tracking-widest text-slate-500 mb-1.5">{children}</div>
);

const TextArea = (props) => (
  <textarea {...props} className="w-full bg-black/40 border border-white/10 rounded-xl px-3 py-2 text-sm text-slate-200 min-h-[90px] focus:border-cyan-400/40 outline-none" />
);

// Editable list of plain strings
const StringList = ({ label, items, onChange, testid }) => (
  <div data-testid={testid}>
    <Label>{label}</Label>
    <div className="space-y-2">
      {items.map((v, i) => (
        <div key={i} className="flex gap-2">
          <Input value={v} onChange={(e) => onChange(items.map((x, j) => (j === i ? e.target.value : x)))}
            className="bg-black/40 border-white/10 text-sm" data-testid={`${testid}-item-${i}`} />
          <Button size="icon" variant="ghost" onClick={() => onChange(items.filter((_, j) => j !== i))}
            className="shrink-0 text-rose-400 hover:text-rose-300" data-testid={`${testid}-del-${i}`}><Trash2 size={14} /></Button>
        </div>
      ))}
      <Button size="sm" variant="ghost" onClick={() => onChange([...items, ""])}
        className="btn-ghost rounded-lg text-xs" data-testid={`${testid}-add`}><Plus size={13} /> Add</Button>
    </div>
  </div>
);

// Editable list of tuples (arrays of fixed length)
const TupleList = ({ label, items, cols, onChange, testid }) => (
  <div data-testid={testid}>
    <Label>{label}</Label>
    <div className="space-y-2">
      {items.map((row, i) => (
        <div key={i} className="flex gap-2 items-start">
          {cols.map((c, ci) => (
            <Input key={ci} placeholder={c} value={row[ci] || ""}
              onChange={(e) => onChange(items.map((r, j) => (j === i ? r.map((x, k) => (k === ci ? e.target.value : x)) : r)))}
              className="bg-black/40 border-white/10 text-sm" data-testid={`${testid}-${i}-${ci}`} />
          ))}
          <Button size="icon" variant="ghost" onClick={() => onChange(items.filter((_, j) => j !== i))}
            className="shrink-0 text-rose-400 hover:text-rose-300" data-testid={`${testid}-del-${i}`}><Trash2 size={14} /></Button>
        </div>
      ))}
      <Button size="sm" variant="ghost" onClick={() => onChange([...items, cols.map(() => "")])}
        className="btn-ghost rounded-lg text-xs" data-testid={`${testid}-add`}><Plus size={13} /> Add</Button>
    </div>
  </div>
);

export const GuideEditor = () => {
  const [peptides, setPeptides] = useState([]);
  const [slug, setSlug] = useState("");
  const [guide, setGuide] = useState(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => { api.get("/peptides").then(({ data }) => setPeptides(data)).catch(() => {}); }, []);

  const load = async (s) => {
    setSlug(s); setGuide(null);
    if (!s) return;
    setLoading(true);
    try {
      const { data } = await api.get(`/peptides/${s}`);
      const g = data.guide ? { ...data.guide } : blankGuide(s);
      delete g._id;
      g.slug = s;
      ["benefits", "side_effects", "important_notes", "lifestyle", "faq", "references", "dosing"].forEach((k) => { if (!Array.isArray(g[k])) g[k] = []; });
      if (!g.quickstart) g.quickstart = { summary: "", bullets: [] };
      setGuide(g);
    } catch { toast.error("Could not load guide"); }
    setLoading(false);
  };

  const set = (patch) => setGuide((g) => ({ ...g, ...patch }));

  const save = async () => {
    setSaving(true);
    try { await api.post("/admin/peptide-guide", guide); toast.success("Guide saved — live immediately"); }
    catch { toast.error("Save failed"); }
    setSaving(false);
  };

  return (
    <div data-testid="guide-editor">
      <div className="flex items-center gap-3 mb-6">
        <BookOpen className="text-cyan-300" size={22} />
        <h1 className="font-serif text-4xl text-white">Guide Editor</h1>
      </div>
      <p className="text-slate-400 text-sm mb-6 max-w-2xl">Edit any peptide's research-guide copy and references. Saved guides are marked custom and will never be overwritten by the content seed.</p>

      <select value={slug} onChange={(e) => load(e.target.value)} data-testid="guide-editor-select"
        className="bg-black/40 border border-white/10 rounded-xl px-3 py-2.5 text-sm text-slate-200 mb-8 min-w-[280px]">
        <option value="">Select a peptide…</option>
        {peptides.map((p) => <option key={p.slug} value={p.slug}>{p.name}</option>)}
      </select>

      {loading && <div className="text-slate-500 font-mono text-sm animate-pulse">Loading guide…</div>}

      {guide && (
        <div className="max-w-3xl space-y-7">
          <div>
            <Label>Guide Title</Label>
            <Input value={guide.title} onChange={(e) => set({ title: e.target.value })}
              className="bg-black/40 border-white/10" data-testid="guide-editor-title" />
          </div>
          <div>
            <Label>Quickstart Summary</Label>
            <TextArea value={guide.quickstart?.summary || ""}
              onChange={(e) => set({ quickstart: { ...guide.quickstart, summary: e.target.value } })}
              data-testid="guide-editor-summary" />
          </div>
          <div>
            <Label>How It Works (Mechanism)</Label>
            <TextArea value={guide.how_it_works || ""} onChange={(e) => set({ how_it_works: e.target.value })}
              data-testid="guide-editor-mechanism" />
          </div>
          <StringList label="Benefits" items={guide.benefits} onChange={(v) => set({ benefits: v })} testid="guide-editor-benefits" />
          <StringList label="Side Effects / Considerations" items={guide.side_effects} onChange={(v) => set({ side_effects: v })} testid="guide-editor-sides" />
          <StringList label="Important Notes" items={guide.important_notes} onChange={(v) => set({ important_notes: v })} testid="guide-editor-notes" />
          <StringList label="Lifestyle Factors" items={guide.lifestyle} onChange={(v) => set({ lifestyle: v })} testid="guide-editor-lifestyle" />
          <TupleList label="Dosing Protocol (label · value)" items={guide.dosing} cols={["Label", "Value"]} onChange={(v) => set({ dosing: v })} testid="guide-editor-dosing" />
          <TupleList label="FAQ (question · answer)" items={guide.faq} cols={["Question", "Answer"]} onChange={(v) => set({ faq: v })} testid="guide-editor-faq" />
          <TupleList label="References (journal · citation · URL)" items={guide.references} cols={["Journal", "Citation", "URL"]} onChange={(v) => set({ references: v })} testid="guide-editor-refs" />

          <div className="sticky bottom-4 flex justify-end">
            <Button onClick={save} disabled={saving} className="btn-primary rounded-xl px-6" data-testid="guide-editor-save">
              <Save size={15} /> {saving ? "Saving…" : "Save Guide"}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};
